import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ACTIVE_HIGHLIGHT_COLOR, SEARCH_HIGHLIGHT_COLOR, HIGHLIGHT_COLOR } from './constants';
import { BoundingBoxWithId } from '@/types';

interface HighlightCanvasProps {
  highlights: BoundingBoxWithId[];
  pageNumber: number;
  width: number;
  height: number;
  highlightedFactId?: string | null;
  onHighlightClick?: (id: string) => void;
  highlightedChunkId?: string | null;
  // When false, the canvas only paints highlights and ignores pointer events.
  interactive?: boolean;
}

export const HighlightCanvas = ({
  highlights,
  pageNumber,
  width,
  height,
  highlightedFactId,
  onHighlightClick,
  highlightedChunkId,
  interactive = true,
}: HighlightCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>, pageNumber: number) => {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const clickedHighlight = highlights.find(
      (h) =>
        h.pageNumber === pageNumber &&
        x >= h.left * width &&
        x <= (h.left + h.width) * width &&
        y >= h.top * height &&
        y <= (h.top + h.height) * height,
    );

    if (clickedHighlight && onHighlightClick) {
      // Only allow clicking on fact highlights, not search highlights
      if (!clickedHighlight.id.startsWith('search-')) {
        onHighlightClick(clickedHighlight.id);
      }
    }
  };

  const pageHighlights = useMemo(() => {
    return highlights.filter((h) => {
      if (h.pageNumber !== pageNumber) return false;

      // For search highlights, only include if they belong to the current chunk
      // Also exclude all search highlights if no chunk is selected
      const isSearchHighlight = h.id.startsWith('search-');
      if (isSearchHighlight && (!highlightedChunkId || !h.id.includes(highlightedChunkId))) {
        return false;
      }

      return true;
    });
  }, [highlights, pageNumber, highlightedChunkId]);

  const updateCanvasCursor = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      if (pageHighlights.length === 0) {
        event.currentTarget.style.cursor = 'default';
        return;
      }

      const isOverHighlight = pageHighlights.some(
        (h) =>
          x >= h.left * width &&
          x <= (h.left + h.width) * width &&
          y >= h.top * height &&
          y <= (h.top + h.height) * height,
      );

      event.currentTarget.style.cursor = isOverHighlight ? 'pointer' : 'default';
    },
    [pageHighlights, width, height],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);

    // Draw highlights for this specific page
    let pageHighlights = highlights.filter((h) => h.pageNumber === pageNumber);
    const highlight = pageHighlights.find((h) => h.id === highlightedFactId);
    if (highlight) {
      pageHighlights.push(pageHighlights.splice(pageHighlights.indexOf(highlight), 1)[0]);
    }

    pageHighlights.forEach((highlight) => {
      const isSearchHighlight = highlight.id.startsWith('search-');

      if (isSearchHighlight && (!highlightedChunkId || !highlight.id.includes(highlightedChunkId))) {
        return;
      }

      context.beginPath();
      context.rect(
        highlight.left * canvas.width,
        highlight.top * canvas.height,
        highlight.width * canvas.width,
        highlight.height * canvas.height,
      );

      if (isSearchHighlight && highlightedChunkId) {
        context.fillStyle = SEARCH_HIGHLIGHT_COLOR;
      } else if (!isSearchHighlight && highlight.id === highlightedFactId) {
        context.fillStyle = ACTIVE_HIGHLIGHT_COLOR;
      } else {
        context.fillStyle = HIGHLIGHT_COLOR;
      }

      context.fill();
    });
  }, [highlights, highlightedFactId, pageNumber, highlightedChunkId, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={`absolute inset-0 z-20 opacity-50 ${interactive ? '' : 'pointer-events-none'}`}
      style={{ width, height }}
      onMouseMove={interactive ? updateCanvasCursor : undefined}
      onClick={interactive ? (e) => handleCanvasClick(e, pageNumber) : undefined}
    />
  );
};
